// skipcq: JS-W1028
import { capturePostHogEvent } from './posthogClient';

/** @typedef {'brewhouse' | 'pint-portfolio' | 'claim' | 'claim-status' | 'review-details'} BrewhouseNavItem */
/** @typedef {'new-reviews' | 'all-reviews'} ReviewDetailsTab */
/** @typedef {'beer' | 'brewery'} ReviewDetailsItemType */
/** @typedef {'recently-added' | 'top-rated'} BrewhouseBeerReviewStatsSection */
/** @typedef {'recentlyAdded' | 'rating' | 'alpha' | 'abvAsc' | 'abvDesc'} PintPortfolioSortMode */
/** @typedef {'profile' | 'reviews'} PintPortfolioBeerProfileTab */
/** @typedef {'mostRecent' | '5' | '4' | '3' | '2' | '1'} PintPortfolioBeerReviewRatingFilter */

/**
 * @param {BrewhouseNavItem} item
 */
export function trackBrewhouseNavClick(item) {
  capturePostHogEvent(`brewhouse-nav-click-${item}`);
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {number} params.recentlyAddedCount
 * @param {number} params.topRatedCount
 */
export function trackBrewhouseBeerReviewStatsView({
  breweryId,
  recentlyAddedCount,
  topRatedCount,
}) {
  capturePostHogEvent('brewhouse-dashboard-view-beer-review-stats', {
    breweryId,
    recentlyAddedCount,
    topRatedCount,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {BrewhouseBeerReviewStatsSection} params.section
 */
export function trackBrewhouseBeerReviewStatsSectionEmpty({ breweryId, section }) {
  capturePostHogEvent('brewhouse-dashboard-beer-review-stats-empty', {
    breweryId,
    section,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 */
export function trackBrewhouseDashboardClickManageBeer({ breweryId }) {
  capturePostHogEvent('brewhouse-dashboard-click-manage-beer', {
    breweryId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 * @param {BrewhouseBeerReviewStatsSection} params.section
 */
export function trackBrewhouseDashboardClickBeerRow({ breweryId, beerId, section }) {
  capturePostHogEvent('brewhouse-dashboard-click-beer-row', {
    breweryId,
    beerId,
    section,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.previousBreweryId
 * @param {number} params.locationCount
 */
export function trackBrewhouseSwitchActiveLocation({
  breweryId,
  previousBreweryId,
  locationCount,
}) {
  capturePostHogEvent('brewhouse-switch-active-location', {
    breweryId,
    previousBreweryId,
    locationCount,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.query
 */
export function trackPintPortfolioSearch({ breweryId, query }) {
  const normalizedQuery = typeof query === 'string' ? query.trim() : '';
  capturePostHogEvent('brewhouse-pint-portfolio-search', {
    breweryId,
    queryLength: normalizedQuery.length,
    hasQuery: normalizedQuery.length > 0,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {PintPortfolioSortMode} params.sortMode
 */
export function trackPintPortfolioSort({ breweryId, sortMode }) {
  capturePostHogEvent('brewhouse-pint-portfolio-sort', {
    breweryId,
    sortMode,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.filterValue
 */
export function trackPintPortfolioFilterLocation({ breweryId, filterValue }) {
  capturePostHogEvent('brewhouse-pint-portfolio-filter-location', {
    breweryId,
    filterValue,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.category
 */
export function trackPintPortfolioFilterStyleCategory({ breweryId, category }) {
  capturePostHogEvent('brewhouse-pint-portfolio-filter-style-category', {
    breweryId,
    category,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.styleName
 */
export function trackPintPortfolioFilterStyleName({ breweryId, styleName }) {
  capturePostHogEvent('brewhouse-pint-portfolio-filter-style-name', {
    breweryId,
    styleName,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 */
export function trackPintPortfolioClickViewDetails({ breweryId, beerId }) {
  capturePostHogEvent('brewhouse-pint-portfolio-click-view-details', {
    breweryId,
    beerId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 */
export function trackPintPortfolioViewBeerProfile({ breweryId, beerId }) {
  capturePostHogEvent('brewhouse-pint-portfolio-view-beer-profile', {
    breweryId,
    beerId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 * @param {PintPortfolioBeerProfileTab} params.tab
 */
export function trackPintPortfolioClickBeerProfileTab({ breweryId, beerId, tab }) {
  capturePostHogEvent('brewhouse-pint-portfolio-click-beer-profile-tab', {
    breweryId,
    beerId,
    tab,
  });
}

/**
 * Fires once per Reviews tab visit after the initial fetch settles.
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 * @param {number} params.reviewCount
 * @param {boolean} params.hasError
 */
export function trackPintPortfolioViewBeerReviews({
  breweryId,
  beerId,
  reviewCount,
  hasError,
}) {
  capturePostHogEvent('brewhouse-pint-portfolio-view-beer-reviews', {
    breweryId,
    beerId,
    reviewCount,
    hasError: Boolean(hasError),
    isEmpty: !hasError && reviewCount === 0,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 * @param {PintPortfolioBeerReviewRatingFilter} params.filterValue
 */
export function trackPintPortfolioFilterBeerReviewsRating({
  breweryId,
  beerId,
  filterValue,
}) {
  capturePostHogEvent('brewhouse-pint-portfolio-filter-beer-reviews-rating', {
    breweryId,
    beerId,
    filterValue,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 * @param {'all' | 'brewery' | 'home'} params.filterValue
 */
export function trackPintPortfolioFilterBeerReviewsConsumptionPlace({
  breweryId,
  beerId,
  filterValue,
}) {
  capturePostHogEvent('brewhouse-pint-portfolio-filter-beer-reviews-consumption-place', {
    breweryId,
    beerId,
    filterValue,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 */
export function trackPintPortfolioClickAddBeer({ breweryId }) {
  capturePostHogEvent('brewhouse-pint-portfolio-click-add-beer', {
    breweryId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 */
export function trackPintPortfolioClickEditBeer({ breweryId, beerId }) {
  capturePostHogEvent('brewhouse-pint-portfolio-click-edit-beer', {
    breweryId,
    beerId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 */
export function trackPintPortfolioSaveBeerAdd({ breweryId, beerId }) {
  capturePostHogEvent('brewhouse-pint-portfolio-save-beer-add', {
    breweryId,
    beerId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {number} params.selectedCount
 */
export function trackAddBeerSelectLocations({ breweryId, selectedCount }) {
  capturePostHogEvent('brewhouse-add-beer-select-locations', {
    breweryId,
    selectedCount,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 */
export function trackAddBeerActiveLocationUnchecked({ breweryId }) {
  capturePostHogEvent('brewhouse-add-beer-active-location-unchecked', {
    breweryId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.matchedBeerId
 */
export function trackAddBeerOfferReuse({ breweryId, matchedBeerId }) {
  capturePostHogEvent('brewhouse-add-beer-offer-reuse', {
    breweryId,
    matchedBeerId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 */
export function trackAddBeerReuseExisting({ breweryId, beerId }) {
  capturePostHogEvent('brewhouse-add-beer-reuse-existing', {
    breweryId,
    beerId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 */
export function trackAddBeerCreateAnyway({ breweryId }) {
  capturePostHogEvent('brewhouse-add-beer-create-anyway', {
    breweryId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 */
export function trackAddBeerRecoveredFromError({ breweryId, beerId }) {
  capturePostHogEvent('brewhouse-add-beer-recovered-from-error', {
    breweryId,
    beerId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 */
export function trackAddBeerTransientUnconfirmed({ breweryId }) {
  capturePostHogEvent('brewhouse-add-beer-transient-unconfirmed', {
    breweryId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 */
export function trackPintPortfolioSaveBeerEdit({ breweryId, beerId }) {
  capturePostHogEvent('brewhouse-pint-portfolio-save-beer-edit', {
    breweryId,
    beerId,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 * @param {string} params.reviewId
 * @param {string} params.reactionType
 */
export function trackPintPortfolioReactReview({
  breweryId,
  beerId,
  reviewId,
  reactionType,
}) {
  capturePostHogEvent('brewhouse-pint-portfolio-react-review', {
    breweryId,
    beerId,
    reviewId,
    reactionType,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {string} params.beerId
 * @param {string} params.reviewId
 */
export function trackPintPortfolioOpenReviewReactions({ breweryId, beerId, reviewId }) {
  capturePostHogEvent('brewhouse-pint-portfolio-open-review-reactions', {
    breweryId,
    beerId,
    reviewId,
  });
}

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} [properties]
 */
function captureReviewDetailsEvent(eventName, properties) {
  try {
    if (typeof properties === 'object' && properties !== null) {
      capturePostHogEvent(eventName, properties);
      return;
    }
    capturePostHogEvent(eventName);
  } catch {
    // Analytics failures must never break the UI.
  }
}

/**
 * Fired when Review Details is shown but the user has not claimed a brewery yet.
 */
export function trackReviewDetailsClaimRequired() {
  captureReviewDetailsEvent('brewhouse-review-details-claim-required');
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {ReviewDetailsTab} params.activeTab
 * @param {number} params.newItemsCount
 * @param {number} params.allItemsCount
 */
export function trackReviewDetailsViewPage({
  breweryId,
  activeTab,
  newItemsCount,
  allItemsCount,
}) {
  captureReviewDetailsEvent('brewhouse-review-details-view-page', {
    breweryId,
    activeTab,
    newItemsCount,
    allItemsCount,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {ReviewDetailsTab} params.tab
 */
export function trackReviewDetailsClickTab({ breweryId, tab }) {
  captureReviewDetailsEvent('brewhouse-review-details-click-tab', {
    breweryId,
    tab,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {ReviewDetailsItemType} params.itemType
 * @param {string} params.itemId
 * @param {number} params.unseenCount
 * @param {ReviewDetailsTab} params.tab
 */
export function trackReviewDetailsSelectItem({
  breweryId,
  itemType,
  itemId,
  unseenCount,
  tab,
}) {
  captureReviewDetailsEvent('brewhouse-review-details-select-item', {
    breweryId,
    itemType,
    itemId,
    unseenCount,
    tab,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {ReviewDetailsItemType} params.itemType
 * @param {string} params.itemId
 * @param {number} params.reviewCount
 * @param {number} params.unseenCount
 * @param {ReviewDetailsTab} params.tab
 */
export function trackReviewDetailsViewItemReviews({
  breweryId,
  itemType,
  itemId,
  reviewCount,
  unseenCount,
  tab,
}) {
  captureReviewDetailsEvent('brewhouse-review-details-view-item-reviews', {
    breweryId,
    itemType,
    itemId,
    reviewCount,
    unseenCount,
    tab,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {number} params.markedCount
 * @param {ReviewDetailsTab} params.tab
 */
export function trackReviewDetailsMarkReviewsSeen({ breweryId, markedCount, tab }) {
  captureReviewDetailsEvent('brewhouse-review-details-mark-reviews-seen', {
    breweryId,
    markedCount,
    tab,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {'beer' | 'hospitality'} params.reviewType
 * @param {string} params.reviewId
 * @param {ReviewDetailsItemType} params.itemType
 * @param {string} [params.reactionType]
 */
export function trackReviewDetailsReactReview({
  breweryId,
  reviewType,
  reviewId,
  itemType,
  reactionType,
}) {
  captureReviewDetailsEvent('brewhouse-review-details-react-review', {
    breweryId,
    reviewType,
    reviewId,
    itemType,
    reactionType,
  });
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {ReviewDetailsTab} params.tab
 * @param {'no-unseen' | 'no-reviews' | 'no-item-reviews' | 'item-reviews-error'} params.reason
 * @param {'beer' | 'brewery'} [params.itemType]
 * @param {string} [params.itemId]
 */
export function trackReviewDetailsEmptyState({
  breweryId,
  tab,
  reason,
  itemType,
  itemId,
}) {
  captureReviewDetailsEvent('brewhouse-review-details-empty-state', {
    breweryId,
    tab,
    reason,
    ...(typeof itemType === 'string' ? { itemType } : {}),
    ...(typeof itemId === 'string' ? { itemId } : {}),
  });
}

export function trackBrewhouseEmailPreferencesLoadError() {
  capturePostHogEvent('brewhouse-email-preferences-load-error');
}

export function trackBrewhouseEmailPreferencesSaveError() {
  capturePostHogEvent('brewhouse-email-preferences-save-error');
}

/**
 * @param {object} params
 * @param {string} params.breweryId
 * @param {'success' | 'miss'} params.outcome
 */
export function trackClaimPrefillBrewery({ breweryId, outcome }) {
  try {
    capturePostHogEvent('brewhouse-claim-prefill-brewery', {
      breweryId,
      outcome,
    });
  } catch {
    // Analytics failures must never break the claim form.
  }
}
